'use client';

import React, { useState } from 'react';

// ─── SHARED COLOR PALETTE ─────────────────────────────────────────────────────
// Biome ring colors → Ayat track colors → Module tile colors (all synced)

export const BIOME_COLORS = {
  forest: '#2d7a47',
  savannah: '#c9a84c',
  grasslands: '#8a8ab8',
  wetlands: '#2d8a87',
  desert: '#c4956a',
  coastal: '#5a8ab8',
  tundra: '#7a9a8a',
  ocean: '#1a5a7a',
} as const;

export const AYAT_TRACK_COLORS = {
  creation: '#2d7a47',     // Ayat in Creation → Forest
  revelation: '#c9a84c',   // Ayat in Revelation → Savannah/Gold
  self: '#8a8ab8',         // Ayat within self → Grasslands/Purple
  community: '#2d8a87',    // Ayat in community → Wetlands
} as const;

export const YAQEEN_COLORS = {
  ilm: '#8a8ab8',    // Knowledge certainty → Purple
  ayn: '#c9a84c',   // Perceptual certainty → Gold
  haqq: '#2d7a47',  // Reality certainty → Forest Green
} as const;

// 8 Forest Layers = 8 IRL Layers = 8 Yaqeen Progression Stages
export const LAYER_SYSTEM = [
  { layer: 1, forest: 'Highland/Canopy', irl: 'Hawas (Sensory Encounter)', yaqeen: 'Ilm — First Contact', color: '#2d7a47' },
  { layer: 2, forest: 'Understory', irl: 'Zahir (Apparent Surface)', yaqeen: 'Ilm — Observation', color: '#3d8a57' },
  { layer: 3, forest: 'Forest Floor', irl: 'Names Fission', yaqeen: 'Ilm → Ayn Transition', color: '#c9a84c' },
  { layer: 4, forest: 'Root Zone', irl: 'Sunnat Allah', yaqeen: 'Ayn — Pattern Recognition', color: '#b89840' },
  { layer: 5, forest: 'Mycorrhizal Network', irl: 'Zahir-Batin Bridge', yaqeen: 'Ayn — Hidden Connections', color: '#8a8ab8' },
  { layer: 6, forest: 'Soil', irl: 'Haqq Recognition', yaqeen: 'Ayn → Haqq Transition', color: '#7a7aa8' },
  { layer: 7, forest: 'Bedrock', irl: 'Amanah', yaqeen: 'Haqq — Direct Experience', color: '#2d8a87' },
  { layer: 8, forest: 'Groundwater', irl: 'Uboor (Crossing)', yaqeen: 'Haqq — Return', color: '#1a7a77' },
] as const;

// ─── SHARED HIKMAH STATION COMPONENT ─────────────────────────────────────────

interface HikmahStationData {
  id: string;
  title: string;
  location: string; // e.g. "Forest → Savannah Transition"
  biomeFrom: keyof typeof BIOME_COLORS;
  biomeTo: keyof typeof BIOME_COLORS;
  layer?: number;
  connections: {
    label: string;
    type: 'ayah' | 'name' | 'observation' | 'community' | 'principle';
    content: string;
  }[];
  flowDiagram?: string;
  namesActive: string[];
  gestureAtPurpose: string;
  mrgBridge?: string;
}

interface HikmahStationProps {
  data: HikmahStationData;
  context?: 'garden' | 'letter' | 'irl'; // Which context this station appears in
  compact?: boolean;
}

export function HikmahStationShared({ data, context = 'letter', compact = false }: HikmahStationProps) {
  const [activeTab, setActiveTab] = useState<'connections' | 'names' | 'gesture'>('connections');
  const fromColor = BIOME_COLORS[data.biomeFrom];
  const toColor = BIOME_COLORS[data.biomeTo];

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: 'rgba(6,13,18,0.8)',
        border: `1px solid ${fromColor}30`,
        boxShadow: `0 0 40px ${fromColor}08`,
      }}
    >
      {/* Header */}
      <div
        className="p-4"
        style={{
          background: `linear-gradient(135deg, ${fromColor}12, ${toColor}08)`,
          borderBottom: `1px solid ${fromColor}20`,
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `${fromColor}20`, border: `1px solid ${fromColor}40` }}
          >
            <span style={{ color: fromColor, fontSize: 14 }}>◈</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[8px] uppercase tracking-widest mb-0.5" style={{ color: `${fromColor}60` }}>
              Hikmah Station · {data.location}
            </p>
            <h4 className="font-mono text-sm font-black uppercase tracking-tight truncate" style={{ color: '#F5F0E8' }}>
              {data.title}
            </h4>
          </div>
          {data.layer && (
            <div
              className="flex-shrink-0 w-7 h-7 rounded flex items-center justify-center font-mono text-[10px] font-black"
              style={{ background: `${LAYER_SYSTEM[data.layer - 1]?.color || fromColor}20`, color: LAYER_SYSTEM[data.layer - 1]?.color || fromColor }}
            >
              {data.layer}
            </div>
          )}
        </div>
        {/* Biome transition indicator */}
        <div className="flex items-center gap-2 mt-3">
          <span className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: `${fromColor}15`, color: fromColor, border: `1px solid ${fromColor}25` }}>
            {data.biomeFrom}
          </span>
          <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 10 }}>→</span>
          <span className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: `${toColor}15`, color: toColor, border: `1px solid ${toColor}25` }}>
            {data.biomeTo}
          </span>
        </div>
      </div>

      {!compact && (
        <>
          {/* Tab nav */}
          <div className="flex" style={{ borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
            {[
              { id: 'connections', label: 'Connections' },
              { id: 'names', label: 'Names' },
              { id: 'gesture', label: 'Gesture' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id as typeof activeTab)}
                className="flex-1 py-2.5 font-mono text-[8px] uppercase tracking-widest transition-all"
                style={{
                  background: activeTab === t.id ? `${fromColor}08` : 'transparent',
                  borderBottom: activeTab === t.id ? `2px solid ${fromColor}` : '2px solid transparent',
                  color: activeTab === t.id ? fromColor : 'rgba(212,197,169,0.3)',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-4">
            {activeTab === 'connections' && (
              <div className="space-y-2">
                {data.connections.map((c, i) => {
                  const typeColors: Record<string, string> = {
                    ayah: '#2d7a47', name: '#c9a84c', observation: '#2d8a87',
                    community: '#8a8ab8', principle: '#c4956a',
                  };
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <span
                        className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5"
                        style={{ background: `${typeColors[c.type]}15`, color: typeColors[c.type], border: `1px solid ${typeColors[c.type]}25` }}
                      >
                        {c.type}
                      </span>
                      <div>
                        <p className="font-mono text-[9px] font-black uppercase tracking-widest mb-0.5" style={{ color: 'rgba(212,197,169,0.6)' }}>{c.label}</p>
                        <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.6 }}>{c.content}</p>
                      </div>
                    </div>
                  );
                })}
                {data.flowDiagram && (
                  <div className="rounded-lg p-3 mt-2" style={{ background: `${fromColor}06`, border: `1px solid ${fromColor}15` }}>
                    <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: `${fromColor}50` }}>Flow</p>
                    <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11 }}>{data.flowDiagram}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'names' && (
              <div className="flex flex-wrap gap-2">
                {data.namesActive.map(n => (
                  <span
                    key={n}
                    className="font-mono text-[9px] px-2 py-1 rounded"
                    style={{ background: 'rgba(201,168,76,0.08)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.2)' }}
                  >
                    {n}
                  </span>
                ))}
              </div>
            )}

            {activeTab === 'gesture' && (
              <div>
                <div className="rounded-xl p-4 mb-3" style={{ background: `${fromColor}08`, border: `1px solid ${fromColor}20` }}>
                  <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: `${fromColor}50` }}>Gesture at Purpose</p>
                  <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 12, lineHeight: 1.7, fontStyle: 'italic' }}>
                    "{data.gestureAtPurpose}"
                  </p>
                </div>
                {data.mrgBridge && (
                  <div className="rounded-lg p-3" style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.15)' }}>
                    <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,138,135,0.6)' }}>MRG Bridge</p>
                    <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11 }}>{data.mrgBridge}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {compact && (
        <div className="p-4">
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.6, fontStyle: 'italic' }}>
            "{data.gestureAtPurpose}"
          </p>
        </div>
      )}
    </div>
  );
}

// ─── EMERGENCE TRIGGER COMPONENT ─────────────────────────────────────────────

interface EmergenceTriggerProps {
  condition: string;
  threshold: string;
  biome: keyof typeof BIOME_COLORS;
  onEmerge?: () => void;
  emerged?: boolean;
}

export function EmergenceTrigger({ condition, threshold, biome, onEmerge, emerged = false }: EmergenceTriggerProps) {
  const color = BIOME_COLORS[biome];

  return (
    <div
      className="rounded-lg p-4 transition-all duration-500"
      style={{
        background: emerged ? `${color}10` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${emerged ? color + '40' : 'rgba(255,255,255,0.06)'}`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
          style={{
            background: emerged ? `${color}30` : 'rgba(255,255,255,0.04)',
            border: `1px solid ${emerged ? color : 'rgba(255,255,255,0.1)'}`,
          }}
        >
          <span style={{ color: emerged ? color : 'rgba(212,197,169,0.3)', fontSize: 10 }}>
            {emerged ? '✦' : '◎'}
          </span>
        </div>
        <div className="flex-1">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-0.5" style={{ color: emerged ? color : 'rgba(212,197,169,0.3)' }}>
            {emerged ? 'Emerged' : 'Emergence Condition'}
          </p>
          <p style={{ color: emerged ? 'rgba(212,197,169,0.8)' : 'rgba(212,197,169,0.45)', fontSize: 12 }}>{condition}</p>
        </div>
        {!emerged && onEmerge && (
          <button
            onClick={onEmerge}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 transition-all flex-shrink-0"
            style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}
          >
            Trigger
          </button>
        )}
      </div>
      {emerged && (
        <p className="font-mono text-[9px] mt-2 pl-9" style={{ color: `${color}60` }}>
          Threshold reached: {threshold}
        </p>
      )}
    </div>
  );
}

// ─── LAYER SYNC DISPLAY ───────────────────────────────────────────────────────

export function LayerSyncDisplay({ compact = false }: { compact?: boolean }) {
  const [activeLayer, setActiveLayer] = useState<number | null>(null);

  return (
    <div>
      {!compact && (
        <div className="mb-4">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>
            8 Forest Layers = 8 IRL Layers = 8 Yaqeen Stages
          </p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>
            The layer numbering is synchronized across all three contexts. Tap a layer to see the correspondence.
          </p>
        </div>
      )}
      <div className="space-y-1.5">
        {LAYER_SYSTEM.map(l => (
          <button
            key={l.layer}
            onClick={() => setActiveLayer(activeLayer === l.layer ? null : l.layer)}
            className="w-full text-left transition-all duration-200"
          >
            <div
              className="flex items-center gap-3 p-3 rounded-lg"
              style={{
                background: activeLayer === l.layer ? `${l.color}12` : 'rgba(255,255,255,0.02)',
                border: `1px solid ${activeLayer === l.layer ? l.color + '40' : 'rgba(255,255,255,0.05)'}`,
              }}
            >
              <span
                className="font-mono text-[10px] font-black w-5 h-5 flex items-center justify-center rounded flex-shrink-0"
                style={{ background: `${l.color}20`, color: l.color }}
              >
                {l.layer}
              </span>
              {compact ? (
                <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: l.color }}>{l.irl}</p>
              ) : (
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: l.color }}>{l.forest}</p>
                  <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.5)' }}>{l.irl}</p>
                  <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>{l.yaqeen}</p>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      {!compact && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: 'Forest Layer', color: '#2d7a47' },
            { label: 'IRL Layer', color: '#c9a84c' },
            { label: 'Yaqeen Stage', color: '#8a8ab8' },
          ].map(h => (
            <p key={h.label} className="font-mono text-[8px] uppercase tracking-widest text-center" style={{ color: `${h.color}60` }}>{h.label}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SAMPLE HIKMAH STATIONS DATA ─────────────────────────────────────────────

export const SAMPLE_HIKMAH_STATIONS: HikmahStationData[] = [
  {
    id: 'forest-savannah',
    title: 'Where the Canopy Meets the Prairie',
    location: 'Forest → Savannah Transition',
    biomeFrom: 'forest',
    biomeTo: 'savannah',
    layer: 3,
    connections: [
      { label: 'Quran 16:11', type: 'ayah', content: '"He causes to grow for you with it, the crops and the olives and the date palms and the grapes and of every kind fruits. Indeed in that surely a Sign for a people who reflect."' },
      { label: 'Al-Musawwir', type: 'name', content: 'The Fashioner — the edge between forest and prairie is where Al-Musawwir\'s design is most visible: two forms meeting, each shaped to its purpose.' },
      { label: 'Ecotone Observation', type: 'observation', content: 'The forest edge is the most biodiverse zone — where two systems meet, life multiplies. This is the Mizan at the boundary.' },
      { label: 'Community Parallel', type: 'community', content: 'Where communities meet — the edge between neighborhoods — is where the most complex social dynamics emerge. What Mizan is maintained at your community\'s edges?' },
    ],
    flowDiagram: 'Canopy shade → Edge light → Prairie sun → Soil transition → Root systems interweave → Nutrient exchange',
    namesActive: ['Al-Musawwir', 'Al-Khaliq', 'Ar-Razzaq', 'Al-Latif'],
    gestureAtPurpose: 'The edge is not a boundary — it is a conversation. Two systems speaking to each other through soil and light. What conversation is happening at the edges of your locality?',
    mrgBridge: 'Map the edges in your ward — where neighborhoods meet, where resources are concentrated or absent. The ecotone principle: the most complex needs emerge at the boundaries.',
  },
  {
    id: 'wetlands-grasslands',
    title: 'Where Water Meets the Deep Roots',
    location: 'Wetlands → Grasslands Transition',
    biomeFrom: 'wetlands',
    biomeTo: 'grasslands',
    layer: 5,
    connections: [
      { label: 'Quran 15:21', type: 'ayah', content: '"And not any thing but with Us its treasures, and We don\'t send down but in a known measure."' },
      { label: 'Al-Latif', type: 'name', content: 'The Subtle — the filtration process in wetlands is Al-Latif made visible: the most subtle, hidden work of purification happening beneath the surface.' },
      { label: 'Filtration Observation', type: 'observation', content: 'Wetland plants filter toxins through their root systems — invisible work that purifies what passes through. The Batin of the wetland is its filtration function.' },
    ],
    flowDiagram: 'Contaminated water → Wetland root zone → Microbial filtration → Clean water → Prairie aquifer → Deep root access',
    namesActive: ['Al-Latif', 'Al-Muhyi', 'Al-Qadir', 'Al-Khabir'],
    gestureAtPurpose: 'The wetland does not announce its work. It filters quietly, invisibly, continuously. What purification work in your community happens without recognition?',
    mrgBridge: 'Environmental health resources — clean water access, soil remediation, community filtration systems. The wetland principle: purification is a community Amanah.',
  },
];

// ─── HIKMAH STATIONS GRID ─────────────────────────────────────────────────────

export function HikmahStationsGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {SAMPLE_HIKMAH_STATIONS.map(station => (
        <HikmahStationShared key={station.id} data={station} context="letter" />
      ))}
    </div>
  );
}
